import React, { useState, useCallback, memo } from 'react';
import PassengerDocumentManager from './PassengerDocumentManager';

/**
 * Optimized FieldRenderer component to prevent input field deselection
 */
const FieldRenderer = memo(({
  label,
  fieldName,
  currentValue,
  type = 'text',
  options = null,
  required = false,
  isEditing,
  onInputChange,
  passengerId
}) => {
  // Format display value based on type
  const getDisplayValue = (value, fieldType) => {
    if (!value) return '';

    if (fieldType === 'date') {
      // Format date for display (YYYY-MM-DD -> readable format)
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
      } catch (e) {
        console.warn('Invalid date format:', value);
      }
    }

    return value;
  };

  if (isEditing) {
    return (
      <div className="p-3 bg-gray-50 rounded-md">
        <label className="block text-sm text-gray-500 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {type === 'select' ? (
          <select
            value={currentValue || ''}
            onChange={(e) => onInputChange(fieldName, e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={currentValue || ''}
            onChange={(e) => onInputChange(fieldName, e.target.value)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={`Enter ${label.toLowerCase()}`}
            autoComplete="off"
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex justify-between p-3 bg-gray-50 rounded-md">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="font-medium">{getDisplayValue(currentValue, type) || 'Not specified'}</span>
    </div>
  );
});

FieldRenderer.displayName = 'FieldRenderer';

/**
 * PassengerDetails component for displaying passenger information with vertical tabs
 * @param {Object} props - Component props
 * @param {Array} props.passengers - Array of passenger data
 * @param {Function} props.onUpdatePassenger - Function to update passenger data
 * @param {Function} props.onDeletePassenger - Function to delete passenger data
 * @param {Function} props.onRefresh - Function to refresh data after operations
 * @returns {JSX.Element} PassengerDetails component
 */
const PassengerDetails = ({ passengers = [], onUpdatePassenger, onDeletePassenger, onRefresh }) => {
  // Ensure we have a valid default value
  const getPassengerId = (passenger) => {
    return passenger.passengerFlightId || passenger.id || `passenger-${passenger.first_name}-${passenger.last_name}`;
  };

  const [selectedPassenger, setSelectedPassenger] = useState(
    passengers.length > 0 ? getPassengerId(passengers[0]) : null
  );

  if (passengers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold mb-6">Passengers</h2>
        <p className="text-gray-500">No passenger information available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-bold mb-6">Passengers</h2>
      <div className="flex gap-6 min-h-[500px]">
        {/* Vertical Tab Navigation */}
        <div className="w-64 flex-shrink-0">
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {passengers.map((passenger) => {
              const passengerId = getPassengerId(passenger);
              const isActive = selectedPassenger === passengerId;
              return (
                <button
                  key={passengerId}
                  onClick={() => setSelectedPassenger(passengerId)}
                  className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {passenger.first_name || 'Unknown'} {passenger.last_name || 'Passenger'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {selectedPassenger && (
            <PassengerInfo
              passenger={passengers.find(p => getPassengerId(p) === selectedPassenger)}
              onUpdatePassenger={onUpdatePassenger}
              onDeletePassenger={onDeletePassenger}
              onRefresh={onRefresh}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * PassengerInfo component for displaying individual passenger details
 * @param {Object} props - Component props
 * @param {Object} props.passenger - Passenger data object
 * @param {Function} props.onUpdatePassenger - Function to update passenger data
 * @param {Function} props.onDeletePassenger - Function to delete passenger data
 * @param {Function} props.onRefresh - Function to refresh data after operations
 * @returns {JSX.Element} PassengerInfo component
 */
const PassengerInfo = ({ passenger, onUpdatePassenger, onDeletePassenger, onRefresh }) => {
  // DEBUG: Log passenger data when component renders
  console.log('=== DEBUG: PassengerInfo received passenger ===');
  console.log('passenger:', passenger);
  console.log('passenger.gender:', passenger.gender);
  console.log('passenger.date_of_birth:', passenger.date_of_birth);
  console.log('passenger.id:', passenger.id);
  console.log('passenger.passengerFlightId:', passenger.passengerFlightId);

  // State for edit mode and form data
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState({ text: '', type: '' }); // type: 'success' or 'error'
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Extract passenger data with fallbacks and edit mode detection
  // const passengerDetails = {
  //   firstName: passenger.first_name || '',
  //   lastName: passenger.last_name || '',
  //   gender: passenger.gender || '',
  //   dateOfBirth: passenger.date_of_birth || passenger.dob || ''
  // };

  // Initialize form data with current passenger data
  React.useEffect(() => {
    console.log('=== DEBUG: Initializing passenger data ===');
    console.log('passenger object:', passenger);
    console.log('passenger.gender:', passenger.gender);
    console.log('passenger.date_of_birth:', passenger.date_of_birth);
    console.log('passenger.dob:', passenger.dob);

    const newFormData = {
      firstName: passenger.first_name || '',
      lastName: passenger.last_name || '',
      gender: passenger.gender || '',
      dateOfBirth: passenger.date_of_birth || passenger.dob || ''
    };

    console.log('=== DEBUG: Initialized formData ===');
    console.log('newFormData:', newFormData);

    setFormData(newFormData);
    setIsEditing(false);
    setMessage({ text: '', type: '' });
  }, [passenger]);

  // Handle input changes with optimization to prevent unnecessary re-renders
  const handleInputChange = useCallback((fieldName, value) => {
    console.log('=== DEBUG: handleInputChange called ===');
    console.log('fieldName:', fieldName);
    console.log('value:', value);

    setFormData(prev => {
      // Only update if the value has actually changed
      if (prev[fieldName] === value) {
        console.log('Value unchanged, not updating formData');
        return prev;
      }

      const newFormData = {
        ...prev,
        [fieldName]: value
      };

      console.log('Updated formData:', newFormData);
      return newFormData;
    });
  }, []);

  // Form validation
  const validateForm = () => {
    const errors = [];

    // Since firstName and lastName are read-only, no validation needed for them
    // Only validate editable fields

    // Validate date format only if dates are provided
    if (formData.dateOfBirth && !isValidDate(formData.dateOfBirth)) {
      errors.push('Please enter a valid date of birth');
    }

    return errors;
  };

  // Helper function to validate date
  const isValidDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  // Handle save changes
  const handleSave = async () => {
    const validationErrors = validateForm();

    if (validationErrors.length > 0) {
      setMessage({
        text: `Validation errors: ${validationErrors.join(', ')}`,
        type: 'error'
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      return;
    }

    if (!onUpdatePassenger) {
      setMessage({ text: 'Update function not available', type: 'error' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage({ text: '', type: '' });

      // Validate that we have a passenger ID
      if (!passenger.id) {
        console.error('Passenger object:', passenger);
        throw new Error('Passenger ID is required for updating individual passenger data');
      }

      console.log('Updating passenger with ID:', passenger.id);

      // DEBUG: Log current formData values
      console.log('=== DEBUG: handleSave formData ===');
      console.log('formData:', formData);
      console.log('formData.gender:', formData.gender);
      console.log('formData.dateOfBirth:', formData.dateOfBirth);
      console.log('formData.firstName:', formData.firstName);
      console.log('formData.lastName:', formData.lastName);

      const updateData = {
        passengerId: passenger.id, // Use passenger.id as the individual passenger ID
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNumber: passenger.mobile_number || passenger.phone_number || "",
        email: passenger.email || "",
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        nationality: passenger.nationality || "",
        countryOfResidence: passenger.country_of_residence || passenger.countryOfResidence || ""
      };

      // DEBUG: Log the data being sent to onUpdatePassenger
      console.log('=== DEBUG: Data being sent to onUpdatePassenger ===');
      console.log('updateData:', updateData);

      // Call the API to update passenger data using the new INDIVIDUAL_PASSENGER endpoint
      await onUpdatePassenger(updateData);

      setMessage({ text: 'Passenger data updated successfully!', type: 'success' });
      setIsEditing(false);

      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }

      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error updating passenger:', error);
      setMessage({
        text: `Failed to update passenger: ${error.message}`,
        type: 'error'
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete passenger
  const handleDelete = async () => {
    if (!onDeletePassenger) {
      setMessage({ text: 'Delete function not available', type: 'error' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage({ text: '', type: '' });

      // Call the API to delete passenger data
      await onDeletePassenger(passenger.passengerFlightId);

      setMessage({ text: 'Passenger deleted successfully!', type: 'success' });
      setShowDeleteConfirm(false);

      // Refresh the data and reload the page
      if (onRefresh) {
        await onRefresh();
      }

      // Force page refresh after successful deletion
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error deleting passenger:', error);
      setMessage({
        text: `Failed to delete passenger: ${error.message}`,
        type: 'error'
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle cancel changes
  const handleCancel = () => {
    const originalFormData = {
      firstName: passenger.first_name || '',
      lastName: passenger.last_name || '',
      gender: passenger.gender || '',
      dateOfBirth: passenger.date_of_birth || passenger.dob || ''
    };
    setFormData(originalFormData);
    setIsEditing(false);
    setMessage({ text: '', type: '' });
  };

  // Gender options
  const genderOptions = [
    { value: '', label: 'Select Gender' },
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
    { value: 'Prefer not to say', label: 'Prefer not to say' }
  ];



  return (
    <div className="space-y-6">
      {/* Header with Edit and Delete Buttons */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900">Passenger Details</h3>
        {!isEditing && (
          <div className="flex space-x-2">
            <button
              onClick={() => setIsEditing(true)}
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center text-sm disabled:opacity-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Message Display */}
      {message.text && (
        <div className={`px-4 py-3 rounded border ${
          message.type === 'success'
            ? 'bg-green-100 border-green-400 text-green-700'
            : 'bg-red-100 border-red-400 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Personal Information Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <FieldRenderer
              label="First Name"
              fieldName="firstName"
              currentValue={formData.firstName}
              required={true}
              isEditing={false} // Always read-only
              onInputChange={handleInputChange}
              passengerId={passenger.passengerFlightId}
            />
            <FieldRenderer
              label="Last Name"
              fieldName="lastName"
              currentValue={formData.lastName}
              required={false}
              isEditing={false} // Always read-only
              onInputChange={handleInputChange}
              passengerId={passenger.passengerFlightId}
            />
          </div>
          <div className="space-y-3">
            <FieldRenderer
              label="Gender/Sex"
              fieldName="gender"
              currentValue={formData.gender}
              type="select"
              options={genderOptions}
              required={false}
              isEditing={isEditing}
              onInputChange={handleInputChange}
              passengerId={passenger.passengerFlightId}
            />
            <FieldRenderer
              label="Date of Birth"
              fieldName="dateOfBirth"
              currentValue={formData.dateOfBirth}
              type="date"
              required={false}
              isEditing={isEditing}
              onInputChange={handleInputChange}
              passengerId={passenger.passengerFlightId}
            />
          </div>
        </div>
      </div>

      {/* Passenger Document Manager */}
      <PassengerDocumentManager
        passengerId={passenger.id}
        existingDocuments={passenger.passengerDocuments || []}
        onDocumentAdded={onRefresh}
        onDocumentUpdated={onRefresh}
      />

      {/* Save/Cancel Buttons */}
      {isEditing && (
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Passenger</h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete passenger "{passenger.first_name} {passenger.last_name}"? This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PassengerDetails;
